import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router } from '@angular/router';
import { ProductGroup } from '@prlw/core/products/product-group.entity';
import { ProductGroupsService } from '@prlw/core/products/product-groups.service';
import { ProductsService } from '@prlw/core/products/products.service';
import { reorderOnOverflow } from '@prlw/libs/reorder-on-overflow/reorder-on-overflow.function';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ProductResizeObserverService } from '../product-resize-observer.service';
import { ProductSelectionDialogService } from '../../monitor/product-selection-dialog/product-selection-dialog.service';
import { ProductGroupFilterOverlayService } from './product-group-filter-overlay.service';
import { MobileOverlayService } from '@prlw/libs/mobile-overlay/mobile-overlay.service';
import { ProductGroupFilterMobileContainer } from '../product-group-filter-mobile/product-group-filter-mobile.container';

const PRODUCT_PILL_WIDTH_WITH_MARGIN = 60;
const FADE_WIDTH = 40;
const INITIAL_FILTER_WIDTH = 1000;

@Component({
  selector: 'prlw-group-product-filter',
  templateUrl: './product-group-filter.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupFilterContainer {
  public readonly selectedProductGroups$: Observable<ProductGroup[]>;
  public readonly currentProductGroup$: Observable<ProductGroup | null>;
  public readonly isEveryProductGroupSelected$: Observable<boolean>;
  public readonly isOverflow$: Observable<boolean>;
  public readonly isOverflowLeft$: Observable<boolean>;
  public readonly isOverflowRight$: Observable<boolean>;
  public readonly doesCurrentGroupHaveProducts$: Observable<boolean>;

  constructor(
    private readonly productGroupsService: ProductGroupsService,
    productsService: ProductsService,
    private readonly productFilterOverlayService: ProductGroupFilterOverlayService,
    private readonly router: Router,
    private readonly productResizeObserverService: ProductResizeObserverService,
    private readonly productSelectionDialogService: ProductSelectionDialogService,
    private readonly mobileOverlayService: MobileOverlayService,
  ) {
    this.currentProductGroup$ = productGroupsService.currentProductGroup$;
    this.selectedProductGroups$ =
      productGroupsService.selectedProductGroups$.pipe(
        reorderOnOverflow(
          PRODUCT_PILL_WIDTH_WITH_MARGIN,
          productResizeObserverService.currentWidth$.pipe(
            startWith(INITIAL_FILTER_WIDTH),
            map((width) => width - PRODUCT_PILL_WIDTH_WITH_MARGIN - FADE_WIDTH), // Account for ALL product pill and fade
          ),
          this.selectedProductGroupIndex$,
        ),
      );
    this.isEveryProductGroupSelected$ =
      productGroupsService.isEveryProductGroupSelected$;
    this.isOverflow$ = productResizeObserverService.isOverflow$;
    this.isOverflowLeft$ = productResizeObserverService.isOverflowLeft$;
    this.isOverflowRight$ = productResizeObserverService.isOverflowRight$;
    this.doesCurrentGroupHaveProducts$ =
      productsService.productsOfCurrentGroup$.pipe(
        map((products) => Boolean(products.length)),
      );
  }

  private get selectedProductGroupIndex$(): Observable<number> {
    return combineLatest([
      this.productGroupsService.selectedProductGroups$,
      this.currentProductGroup$,
    ]).pipe(
      map(([selectedProductGroups, currentProductGroup]) =>
        currentProductGroup !== null
          ? selectedProductGroups.indexOf(currentProductGroup)
          : 0,
      ),
    );
  }

  public setInit(element: HTMLElement): void {
    this.productResizeObserverService.initResizeObserver(element);
  }

  public openOverlay(): void {
    this.productFilterOverlayService.show();
  }

  public handleToggleMobileOverlay() {
    this.mobileOverlayService.show(ProductGroupFilterMobileContainer);
  }

  public openSelectionDialog(): void {
    this.productSelectionDialogService.open();
  }

  public get isAllSelected(): boolean {
    return this.router.isActive('/monitor/all', {
      paths: 'exact',
      queryParams: 'exact',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  public updateOverflow(element: HTMLElement): void {
    this.productResizeObserverService.updateOverflow(element);
  }
}
